import React, { useCallback, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import { useShowTasks } from '@/hooks/useShowTasks'
import TasksTable from '../../DetailDashboard/components/TasksTable'
import TaskItemsTable from '../../DetailDashboard/components/TaskItemsTable'
import TaskBudgetsTable from '../../DetailDashboard/components/TaskBudgetsTable'
import TaskPerformancesTable from '../../DetailDashboard/components/TaskPerformancesTable'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import TaskItemCreatePanel from '@/pages/project/TaskManagement/components/TaskItemCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import { useTaskEditFromReport } from '@/hooks/useTaskEditFromReport'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import { TaskReportService } from '@/api'

type ListType = 'work' | 'task' | 'budget' | 'performance'

interface TaskListSectionProps {
  workspaceId?: string
  /** 조회 범위 (스냅샷 시점은 X-As-Of 헤더로 별도 전달) */
  startDate?: Date | null
  endDate?: Date | null
  /** 부서 필터 */
  departmentId?: string
  /** 사업 필터 */
  businessId?: string
  /** 목록 타이틀 앞에 붙일 접두어 (예: '기획팀' → '기획팀 수행업무 목록') */
  titlePrefix?: string
  /** 목록 타이틀 맨 뒤에 붙일 접미어 (예: 조회 기간 '(2026년 9월 1일 ~ 2026년 9월 30일)') */
  titleSuffix?: string
}

const LIST_TITLE: Record<ListType, string> = {
  work: '수행업무 목록',
  task: '세부실행과제 목록',
  budget: '예산 목록',
  performance: '성과 목록',
}

const TABS: Array<{ key: ListType; label: string }> = [
  { key: 'work', label: '수행업무' },
  { key: 'task', label: '세부실행과제' },
  { key: 'budget', label: '예산' },
  { key: 'performance', label: '성과' },
]

const ChevronIcon: React.FC = () => (
  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ color: designTokens.colors.gray[4] }}>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
)

const STATUS_SELECT_CLASS = 'h-10 px-3 border-2 rounded cursor-pointer outline-none appearance-none pr-8'
const statusSelectStyle: React.CSSProperties = {
  backgroundColor: designTokens.colors.white,
  borderColor: designTokens.colors.gray[1],
  fontFamily: designTokens.typography.fontFamily.sans[0],
  fontSize: designTokens.typography.fontSize['body-medium'][0],
  color: designTokens.colors.gray[6],
  minWidth: '120px',
}

/** 상세 현황의 목록 UI (탭/상태필터/검색 + 4종 테이블) — 업무 진행 현황 섹션용 */
const TaskListSection: React.FC<TaskListSectionProps> = ({
  workspaceId,
  startDate,
  endDate,
  departmentId,
  businessId,
  titlePrefix,
  titleSuffix,
}) => {
  const wsId = workspaceId || ''

  const [selectedListType, setSelectedListType] = useState<ListType>('work')

  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 세부실행과제 탭을 빼고, 선택돼 있었다면 수행업무로 옮긴다
  const showTasks = useShowTasks()
  const visibleTabs = showTasks ? TABS : TABS.filter(tab => tab.key !== 'task')
  useEffect(() => {
    if (!showTasks && selectedListType === 'task') {
      setSelectedListType('work')
    }
  }, [showTasks, selectedListType])

  // 검색
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchTrigger, setSearchTrigger] = useState(0)

  // 상태 필터 (탭별)
  const [taskItemStatusFilter, setTaskItemStatusFilter] = useState('')
  const [taskStatusFilter, setTaskStatusFilter] = useState('')
  const [budgetStatusFilter, setBudgetStatusFilter] = useState('')
  const [performanceStatusFilter, setPerformanceStatusFilter] = useState('')

  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput.trim())
    setSearchTrigger(prev => prev + 1)
  }, [searchInput])

  const handleListTypeChange = (listType: ListType) => {
    setSelectedListType(listType)
    setSearchInput('')
    setSearchQuery('')
  }

  // ===== 행 클릭 → 항목 상세 패널 =====
  const [isTaskDetailPanelOpen, setIsTaskDetailPanelOpen] = useState(false)
  const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState<string | null>(null)
  const [isTaskItemDetailPanelOpen, setIsTaskItemDetailPanelOpen] = useState(false)
  const [selectedTaskItemIdForDetail, setSelectedTaskItemIdForDetail] = useState<string | null>(null)
  const [isBudgetDetailPanelOpen, setIsBudgetDetailPanelOpen] = useState(false)
  const [selectedBudgetTaskIdForDetail, setSelectedBudgetTaskIdForDetail] = useState<string | null>(null)
  const [isPerformanceDetailPanelOpen, setIsPerformanceDetailPanelOpen] = useState(false)
  const [selectedPerformanceTaskIdForDetail, setSelectedPerformanceTaskIdForDetail] = useState<string | null>(null)

  const handleTaskRowClick = useCallback((task: TaskTreeNode) => {
    if (!task?.id) return
    setSelectedTaskIdForDetail(task.id)
    setIsTaskDetailPanelOpen(true)
  }, [])
  const handleTaskItemClick = useCallback((taskItem: TaskTreeNode) => {
    if (!taskItem?.id) return
    setSelectedTaskItemIdForDetail(taskItem.id)
    setIsTaskItemDetailPanelOpen(true)
  }, [])
  const handleBudgetItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedBudgetTaskIdForDetail(taskId)
    setIsBudgetDetailPanelOpen(true)
  }, [])
  const handlePerformanceItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedPerformanceTaskIdForDetail(taskId)
    setIsPerformanceDetailPanelOpen(true)
  }, [])

  // ===== 상세 패널 "수정" 클릭 → 수정 사이드패널 (OverallDashboard와 동일 패턴) =====
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [editTaskData, setEditTaskData] = useState<{
    id: string
    name: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    assigneeId: string
    assigneeName: string
    subAssigneeId: string
    subAssigneeName: string
    managerId: string
    managerName: string
    reviewerId: string
    reviewerName: string
    departmentId: string
    departmentName: string
    startAt: string
    endAt: string
    description: string
    labels?: Array<{ id: string; business_label_id: string; key: string }>
    detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
    extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    /** AI 분석 프롬프트에 함께 실리는 판단근거 */
    judgmentBasis?: string
    /** 편집 패널의 budget/performance 초기값에 사용 (raw originalData) */
    taskOriginal?: any
  } | null>(null)
  const [isTaskItemEditPanelOpen, setIsTaskItemEditPanelOpen] = useState(false)
  const [editTaskItemData, setEditTaskItemData] = useState<{
    id: string
    name: string
    taskId: string
    taskName: string
    taskSubgroupName: string
    businessName: string
    startAt: string
    endAt: string
    output: string
  } | null>(null)
  // 수정 성공 시 증가 → 상세 패널이 자체 재조회
  const [taskDetailRefreshKey, setTaskDetailRefreshKey] = useState(0)
  // 수정/삭제 후 현재 목록을 다시 조회하기 위한 트리거 (네 종류 목록 공용)
  const [listRefreshTrigger, setListRefreshTrigger] = useState(0)

  const handleTaskEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task' || !task.originalData) return
    const taskData = task.originalData as any
    setEditTaskData({
      id: task.id,
      name: task.name,
      taskSubgroupId: taskData.task_subgroup_id || '',
      taskSubgroupName: taskData.task_subgroup_name || '',
      taskGroupName: taskData.task_group_name || '',
      businessName: task.business_name || '',
      businessId: taskData.business_id || '',
      assigneeId: taskData.assignee?.id || '',
      assigneeName: taskData.assignee?.name || '',
      subAssigneeId: taskData.sub_assignee?.id || '',
      subAssigneeName: taskData.sub_assignee?.name || '',
      managerId: taskData.manager?.id || '',
      managerName: taskData.manager?.name || '',
      reviewerId: taskData.reviewer?.id || '',
      reviewerName: taskData.reviewer?.name || '',
      departmentId: taskData.department_id || '',
      departmentName: task.department_name || '',
      startAt: taskData.start_at || '',
      endAt: taskData.end_at || '',
      description: taskData.description || '',
      labels: taskData.labels || [],
      detailInfos: taskData.detail_infos || [],
      extraInfos: taskData.extra_infos || [],
      judgmentBasis: taskData.judgment_basis || '',
      taskOriginal: taskData
    })
    setIsTaskEditPanelOpen(true)
    // 상세 패널은 열린 상태 유지 — 수정 패널이 위(z)에 뜬다
  }, [])

  const handleTaskItemEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task_item' || !task.originalData) return
    const taskItemData = task.originalData as any
    setEditTaskItemData({
      id: task.id,
      name: task.name,
      taskId: taskItemData.task_id || '',
      taskName: taskItemData.task_name || '',
      taskSubgroupName: taskItemData.task_subgroup_name || '',
      businessName: task.business_name || '',
      startAt: taskItemData.start_at || '',
      endAt: taskItemData.end_at || '',
      output: taskItemData.output || ''
    })
    setIsTaskItemEditPanelOpen(true)
  }, [])

  const handleTaskEditPanelClose = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
  }, [])

  const handleTaskEditSuccess = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
    // 상세 패널 + 현재 목록 재조회
    setTaskDetailRefreshKey(k => k + 1)
    setListRefreshTrigger(t => t + 1)
  }, [])

  const handleTaskItemEditPanelClose = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
  }, [])

  const handleTaskItemEditSuccess = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
    setTaskDetailRefreshKey(k => k + 1)
    setListRefreshTrigger(t => t + 1)
  }, [])

  // ===== 보고서 패널 =====
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null,
  })
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  const fetchReportTemplates = useCallback(async (id: string, businessId?: string | null) => {
    if (!id) return null
    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)
    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId: id,
        businessId: businessId || undefined,
      })
      setReportTemplates(response || [])
      return response || []
    } catch (error) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿 목록을 불러오는데 실패했습니다.')
      return null
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }, [])

  const handleReportClick = useCallback(async (task: TaskTreeNode) => {
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)
    setIsTaskDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)
    if (wsId) {
      const businessId = (task.originalData as any)?.business_id || (task as any).business_id
      const templates = await fetchReportTemplates(wsId, businessId)
      if (templates && templates.length > 0) {
        const first = templates[0]
        setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: { key: first.key, name: first.name } })
      } else {
        setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
      }
    } else {
      setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
    }
  }, [wsId, fetchReportTemplates])

  const handleReportDetailPanelClose = useCallback(() => {
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
  }, [])

  // 보고서 패널의 "세부실행과제 수정" → 보고서 패널을 닫고 수정 패널을 연다. 저장 후 목록 재조회
  const handleReportTaskEditSuccess = useCallback(() => {
    setListRefreshTrigger(t => t + 1)
  }, [])
  const { openTaskEdit: handleReportTaskEdit, taskEditPanel: reportTaskEditPanel } = useTaskEditFromReport({
    onBeforeOpen: handleReportDetailPanelClose,
    onSuccess: handleReportTaskEditSuccess,
  })
  const handleReportTemplateChange = useCallback((templateKey: string, templateName: string) => {
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: templateKey ? { key: templateKey, name: templateName } : null,
    }))
  }, [])
  const handleReportTaskDetail = useCallback(() => {
    const task = reportPanelState.selectedTask
    if (!task?.id) return
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
    setSelectedTaskIdForDetail(task.id)
    setIsTaskDetailPanelOpen(true)
  }, [reportPanelState.selectedTask])

  return (
    <>
      {/* 목록 타이틀 + 필터/검색/탭 */}
      <div className="mt-6 flex-shrink-0">
        <div
          className="flex items-center justify-between gap-[10px] py-[10px]"
          style={{ borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
        >
          <div className="flex items-center gap-[18px]">
            <h2
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '20px',
                lineHeight: '1.193359375em',
                fontWeight: 700,
                color: '#1B1B1B',
              }}
            >
              {titlePrefix ? `${titlePrefix} ` : ''}{LIST_TITLE[selectedListType]}{titleSuffix ? ` ${titleSuffix}` : ''}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* 상태 필터 */}
            {selectedListType === 'work' && (
              <div className="relative">
                <select value={taskItemStatusFilter} onChange={(e) => setTaskItemStatusFilter(e.target.value)} className={STATUS_SELECT_CLASS} style={statusSelectStyle}>
                  <option value="">전체</option>
                  <option value="incomplete">미완료 (담당자)</option>
                  <option value="assigned">미완료 (관리자)</option>
                  <option value="completed">완료 (관리자)</option>
                  <option value="reviewed">완료 (검토자)</option>
                </select>
                <ChevronIcon />
              </div>
            )}
            {selectedListType === 'task' && (
              <div className="relative">
                <select value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)} className={STATUS_SELECT_CLASS} style={statusSelectStyle}>
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <ChevronIcon />
              </div>
            )}
            {selectedListType === 'budget' && (
              <div className="relative">
                <select value={budgetStatusFilter} onChange={(e) => setBudgetStatusFilter(e.target.value)} className={STATUS_SELECT_CLASS} style={statusSelectStyle}>
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <ChevronIcon />
              </div>
            )}
            {selectedListType === 'performance' && (
              <div className="relative">
                <select value={performanceStatusFilter} onChange={(e) => setPerformanceStatusFilter(e.target.value)} className={STATUS_SELECT_CLASS} style={statusSelectStyle}>
                  <option value="">전체</option>
                  <option value="incomplete">미완료</option>
                  <option value="completed">완료</option>
                </select>
                <ChevronIcon />
              </div>
            )}

            {/* 검색 */}
            <div className="flex items-center gap-2">
              <div className="relative w-[300px]">
                <div
                  className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
                  style={{ backgroundColor: designTokens.colors.white, borderColor: designTokens.colors.gray[1] }}
                >
                  <Search className="w-5 h-5" style={{ color: designTokens.colors.gray[3] }} />
                  <input
                    type="text"
                    placeholder="명칭으로 검색해주세요."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="flex-1 bg-transparent outline-none"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                      color: searchInput ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                  />
                </div>
              </div>
              <Button color="dark" size="medium" onClick={handleSearch} className="h-10 px-[10px]">검색</Button>
            </div>

            {/* 탭 메뉴 */}
            <div className="flex" style={{ borderRadius: '4px', overflow: 'hidden' }}>
              {visibleTabs.map((tab, index) => (
                <div
                  key={tab.key}
                  className="flex items-center justify-center cursor-pointer transition-all duration-150"
                  style={{
                    width: '120px',
                    height: '40px',
                    backgroundColor: selectedListType === tab.key ? '#2F4062' : '#EBEBEB',
                    borderRight: index < visibleTabs.length - 1 ? '1px solid #BDBDBD' : undefined,
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '18px',
                    fontWeight: 600,
                    color: selectedListType === tab.key ? '#EBEBEB' : '#707071',
                  }}
                  onClick={() => handleListTypeChange(tab.key)}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 테이블 */}
      {selectedListType === 'work' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskItemsTable
            refreshTrigger={listRefreshTrigger}
            workspaceId={wsId}
            selectedBusinessId={businessId}
            selectedDepartmentId={departmentId}
            isVisible={true}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={taskItemStatusFilter || undefined}
            onTaskItemClick={handleTaskItemClick}
          />
        </div>
      )}
      {selectedListType === 'task' && (
        <div className="mt-6 flex-1 min-h-0">
          <TasksTable
            workspaceId={wsId}
            selectedBusinessId={businessId}
            selectedDepartmentId={departmentId}
            isVisible={true}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={taskStatusFilter || undefined}
            onTaskClick={handleTaskRowClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
            refreshTrigger={listRefreshTrigger}
          />
        </div>
      )}
      {selectedListType === 'budget' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskBudgetsTable
            refreshTrigger={listRefreshTrigger}
            workspaceId={wsId}
            selectedBusinessId={businessId}
            selectedDepartmentId={departmentId}
            isVisible={true}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={budgetStatusFilter || undefined}
            onBudgetItemClick={handleBudgetItemClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
          />
        </div>
      )}
      {selectedListType === 'performance' && (
        <div className="mt-6 flex-1 min-h-0">
          <TaskPerformancesTable
            refreshTrigger={listRefreshTrigger}
            workspaceId={wsId}
            selectedBusinessId={businessId}
            selectedDepartmentId={departmentId}
            isVisible={true}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            searchTrigger={searchTrigger}
            status={performanceStatusFilter || undefined}
            onPerformanceItemClick={handlePerformanceItemClick}
            onReportClick={handleReportClick}
            isActionButtonClicked={isActionButtonClicked}
          />
        </div>
      )}

      {/* 항목 상세 패널 */}
      <TaskDetailPanel
        isOpen={isTaskDetailPanelOpen}
        onClose={() => { setIsTaskDetailPanelOpen(false); setSelectedTaskIdForDetail(null) }}
        taskId={selectedTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isTaskItemDetailPanelOpen}
        onClose={() => { setIsTaskItemDetailPanelOpen(false); setSelectedTaskItemIdForDetail(null) }}
        taskId={selectedTaskItemIdForDetail}
        taskType="task_item"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskItemEdit}
      />
      <TaskDetailPanel
        isOpen={isBudgetDetailPanelOpen}
        onClose={() => { setIsBudgetDetailPanelOpen(false); setSelectedBudgetTaskIdForDetail(null) }}
        taskId={selectedBudgetTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isPerformanceDetailPanelOpen}
        onClose={() => { setIsPerformanceDetailPanelOpen(false); setSelectedPerformanceTaskIdForDetail(null) }}
        taskId={selectedPerformanceTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />

      {/* 보고서 상세 패널 */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={wsId}
        onReportTypeChange={handleReportTemplateChange}
        onTaskClick={handleReportTaskDetail}
        onEditTask={handleReportTaskEdit}
      />

      {/* 세부실행과제 수정 패널 — 보고서 패널의 "세부실행과제 수정" 클릭 시 */}
      {reportTaskEditPanel}

      {/* 수행업무 수정 패널 — 상세 패널의 "수정" 클릭 시 (상세 패널은 열린 상태 유지) */}
      {editTaskItemData && (
        <TaskItemCreatePanel
          isOpen={isTaskItemEditPanelOpen}
          onClose={handleTaskItemEditPanelClose}
          taskId={editTaskItemData.taskId}
          taskName={editTaskItemData.taskName}
          taskSubgroupName={editTaskItemData.taskSubgroupName}
          businessName={editTaskItemData.businessName}
          onSuccess={handleTaskItemEditSuccess}
          editMode={true}
          taskItemId={editTaskItemData.id}
          initialTaskItemData={{
            name: editTaskItemData.name,
            startAt: editTaskItemData.startAt,
            endAt: editTaskItemData.endAt,
            output: editTaskItemData.output
          }}
        />
      )}

      {/* 세부실행과제 수정 패널 — budget/performance 초기값은 taskOriginal에서 추출 */}
      {editTaskData && (() => {
        const taskOriginal = editTaskData.taskOriginal
        return (
          <TaskCreatePanel
            isOpen={isTaskEditPanelOpen}
            onClose={handleTaskEditPanelClose}
            taskSubgroupId={editTaskData.taskSubgroupId}
            taskSubgroupName={editTaskData.taskSubgroupName}
            taskGroupName={editTaskData.taskGroupName}
            businessName={editTaskData.businessName}
            businessId={editTaskData.businessId}
            onSuccess={handleTaskEditSuccess}
            editMode={true}
            taskId={editTaskData.id}
            initialTaskData={{
              name: editTaskData.name,
              assigneeId: editTaskData.assigneeId,
              assigneeName: editTaskData.assigneeName,
              subAssigneeId: editTaskData.subAssigneeId,
              subAssigneeName: editTaskData.subAssigneeName,
              managerId: editTaskData.managerId,
              managerName: editTaskData.managerName,
              reviewerId: editTaskData.reviewerId,
              reviewerName: editTaskData.reviewerName,
              departmentId: editTaskData.departmentId,
              departmentName: editTaskData.departmentName,
              startAt: editTaskData.startAt,
              endAt: editTaskData.endAt,
              description: editTaskData.description,
              budget: (taskOriginal as any)?.budget ? {
                id: (taskOriginal as any).budget.id || '',
                resultDescription: (taskOriginal as any).budget.result_description || '',
                items: ((taskOriginal as any).budget.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              performance: (taskOriginal as any)?.performance ? {
                id: (taskOriginal as any).performance.id || '',
                name: (taskOriginal as any).performance.name || '',
                unit: (taskOriginal as any).performance.unit || '',
                items: ((taskOriginal as any).performance.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              labels: editTaskData.labels,
              detailInfos: editTaskData.detailInfos,
              extraInfos: editTaskData.extraInfos,
              judgmentBasis: editTaskData.judgmentBasis
            }}
          />
        )
      })()}
    </>
  )
}

export default TaskListSection
